<template>
    <div class="marketing-site-editor-wrapper">
        <marketing-site-header
            :template="templateGroup"
            :save-disabled="!canSave"
            :is-saving="isSaving && !isPublishing"
            @close-editor="goToList"
            @update="handleSave"
            @save-template="handleSaveAndExit"
            @publish="openPublishTemplateGroup"
            @save-as-a-template="handleSaveTemplate"
            @share-feedback="handleShareFeedback"
        />
        <div class="marketing-site-editor">
            <div v-if="!templateGroup || !unlayerProjectId" class="loading-label">
                <ds-spinner />
            </div>
            <div v-else>
                <create-custom-field-modal
                    v-if="isCreatingCustomField"
                    :custom-fields="customFields"
                    @close="onCloseCustomFieldModal"
                    @new-field-added="onCreateCustomField"
                />
                <unlayer-editor
                    :options="unlayerOptions"
                    :editor-version="editorVersion"
                    :design="currentTemplateGroupContent"
                    :project-id="unlayerProjectId"
                    :max-page-count="25"
                    @design-update="onUpdateDesign"
                    @unlayer-message="onUnlayerMessage"
                    @register-provider="registerProvider"
                    @fetch-images="fetchImages"
                    @handle-image-upload="handleImageUpload"
                    @handle-image-deleted="handleImageDeleted"
                />
                <publish-details-modal
                    v-if="isPublishTemplateGroupOpen"
                    :custom-domain="customDomain"
                    :is-saving="isSaving"
                    :is-publishing="isPublishing"
                    :has-changes="hasChanges"
                    :keap-link-url="keapLinkUrl"
                    :marketing-site-embed-code="marketingSiteEmbedCode"
                    :domain-settings-url="domainSettingsUrl"
                    :status="templateGroup.templateGroupStatus"
                    :is-published="isPublished"
                    @goToList="goToList"
                    @closeModal="closePublishTemplateGroupModal"
                    @publish="handlePublish"
                />
                <create-tag-modal
                    :is-open="isTagModalOpen"
                    :tag-name="tagModalTagName"
                    @tagCreated="handleCreateTag"
                    @close="cancelCreateTag"
                />
                <ds-modal
                    class="save-as-template-modal"
                    :is-open="isSaveAsTemplateOpen"
                    :title="$t('landingPages.saveAsTemplateModal.label')"
                    show-close
                    size="med"
                    @close="closeSaveAsTemplateModal"
                >
                    <form @submit.prevent="() => onSubmitSaveAsTemplate()">
                        <ds-input-field
                            v-model="saveAsTemplateName"
                            data-qa="template-name-input"
                            required
                            :label="$t('landingPages.saveAsTemplateModal.placeholder')"
                            :readonly="isSavingTemplate"
                        />

                        <ds-spinner v-if="isSavingTemplate" data-qa="template-saving-spinner" />

                        <ds-filled-button
                            v-else
                            :disabled="!saveAsTemplateName"
                            data-qa="template-save-button"
                            type="submit"
                        >
                            {{ $t('global.save') }}
                        </ds-filled-button>
                    </form>
                </ds-modal>
            </div>
        </div>

        <share-feedback-modal
            :is-open="isFeedbackModalOpen"
            :feature="$options.MARKETING_SITES"
            @close="handleCloseFeedback"
        />
    </div>
</template>

<script>
import { UnlayerEditor } from '@infusionsoft/keap-unlayer-editor';
import {
    BLANK_DESIGN,
    buildHashParameters,
    buildMarketingPageUrl,
    buildMarketingSiteEmbedCode,
    buildMarketingSiteUrl,
    buildPageHtml,
    cloneSiteFromSite,
    convertCustomFieldToUnlayerField,
    convertLinksInDesign,
    convertLinksInHtml,
    DEFAULT_LABELS,
    generatePageSlug,
    getTotalFormCount,
    updateFormId,
    decodeHtml,
    clearKeapFormsFromPages
} from '../util/marketing/site.utils';

import {
    createCustomField,
    createTemplateForTemplateGroup,
    deleteTemplate,
    getContactTags,
    getCustomDomainsForTenant,
    getCustomFields,
    getTemplateGroup,
    publishTemplateGroup,
    updateTemplate,
    updateTemplateGroup,
} from '../util/marketing/marketingSiteEditorApi';

import amplitude from '@/components/util/analytics/amplitude';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import CreateCustomFieldModal from './CreateCustomFieldModal';
import CreateTagModal from '@/components/tags/CreateTagModal';
import MarketingSiteHeader from './MarketingSiteHeader';
import PublishDetailsModal from './PublishDetailsModal';
import { MARKETING_SITES } from '@/components/shared/constants/shareFeedback.constants';
import { DOMAIN_SETTINGS_URL } from '../unlayer/constants/unlayer.constants';
import { GALLERY_ROUTE_URL, SITE_STATUS, MARKETING_SITE_UNLAYER_VERSION } from '../util/marketing/unlayer.constants';
import { updateCustomFormsWithinPage } from '../util/marketing/customForm.utils';
import { convertUnlayerFormsToKeapForms } from '../unlayer/UnlayerFormConverters';
import axios from 'axios';
import { mergeCategoryWithTag } from '@/components/unlayer/unlayerEmail.util';

export const AMPLITUDE_EVENT_DISMISSED_FEEDBACK = {
    'Event Source': 'share feedback modal closed without comments',
};
export const TEMPLATE_GROUP_LIST = '/app/marketingsite/home';

export const ERROR_PUBLISH = 'There was a problem publishing your landing page';
export const ERROR_COPYING = 'There was a problem copying to the clipboard';
export const ERROR_SAVE_AS_TEMPLATE = 'There was a problem saving as a template';
export const COPY_CLIPBOARD = 'The text was copied to your clipboard';

const IMAGES_PAGE_SIZE = 25;

const MESSAGES = {
    UPLOAD_ERROR: 'There was a problem trying to upload your image. Please try again.',
    FETCH_IMAGES_ERROR: 'There was a problem trying to fetch your images.',
    DELETE_ERROR: 'There was a problem trying to delete your image. Please try again.',
};

export default {
    MARKETING_SITES,
    DOMAIN_SETTINGS_URL,
    name: 'MarketingSiteEditor',

    components: {
        CreateCustomFieldModal,
        CreateTagModal,
        MarketingSiteHeader,
        PublishDetailsModal,
        ShareFeedbackModal,
        UnlayerEditor,
    },
    props: {
        templateMgmtServiceUrl: {
            type: String,
            default: 'https://template-mgmt.intg.keapapis.com',
        },
        siteId: {
            type: String,
        },
        sites: {
            type: String,
        },
        tenantId: {
            type: String,
        },
        ffUploadSaveOptionEnabled: Boolean,
        ffUnlayerCreateTagEnabled: Boolean,
        ffReorderListNoContentEnabled: Boolean,
        unlayerBuilderVersionOverride: String,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
    },

    data() {
        return {
            isLoading: false,
            isSaving: false,
            isSavingTemplate: false,
            isPublishing: false,
            isFetchingTemplateGroup: true,
            isSaveAsTemplateOpen: false,
            isPublishTemplateGroupOpen: false,
            showPublishedDetails: false,
            customDomain: null,
            saveAsTemplateName: '',
            templateGroup: null,
            originalTemplateGroup: null,
            currentPageId: null,
            editorProvider: null,
            hasChanges: false,
            currentPageData: null,
            customFields: [],
            tagData: [],
            isCreatingCustomField: false,
            customFieldUnlayerResponse: null,
            fieldMax: 4, // TODO: find out where this value comes from (appSettings in keap web)
            i18n: { t: (key, options = { defaultMessage: '' }) => DEFAULT_LABELS[key] || DEFAULT_LABELS[`unlayer.${key}`] || options.defaultMessage },
            isFeedbackModalOpen: false,
            appId: this.tenantId,
            createTagUnlayerResponse: null,
            isTagModalOpen: false,
            tagModalTagName: '',
        };
    },

    mounted() {
        const requests = [];

        requests.push(this.getCustomFields());
        requests.push(this.getTags());

        if (this.siteId) {
            requests.push(this.fetchAndInitializeTemplateGroup(this.siteId));
            requests.push(this.fetchCustomDomain(this.siteId));
        }

        Promise.all(requests).then(() => {
            this.isFetchingTemplateGroup = false;
        });
    },

    computed: {
        editorVersion() {
            return this.useUnlayerBuilderVersionOverrideEnabled && this.unlayerBuilderVersionOverride ? this.unlayerBuilderVersionOverride : MARKETING_SITE_UNLAYER_VERSION;
        },

        unlayerOptions () {
            const {
                keapFormFields,
                i18n,
                appId,
                currentPageId,
                marketingPages,
                templateGroup,
                tags,
                domainSettingsUrl,
                localeData,
            } = this;

            const options = {
                keapFormFields,
                tags,
                i18n,
                appId,
                currentPageId,
                marketingPages,
                site: this.mapTemplateGroupToSite(templateGroup),
                features: [
                    'pages-panel',
                    'publish-settings',
                    'analytics-settings',
                    'settings-panel',
                    'form-tool',
                    'add-form-id',
                    'audit',
                    'page-anchors',
                    'autonameSection',
                    'favicon-settings',
                    'form-tag-create',
                    ...this.ffUploadSaveOptionEnabled ? ['upload-panel'] : [],
                    'url-params',
                ],
                domainSettingsUrl,
                locale: 'en-us',
                localeData: { 'en-us': localeData },
                isCarouselEnabled: false,
            };

            return options;
        },

        marketingSitesList() {
            return JSON.parse(this.sites).filter((site) => site.status === 'ACTIVE' && site.id !== this.siteId);
        },

        unlayerProjectId() { return window.unlayerProjectId; },
        currentEnv() { return window.currentEnv; },
        keapFormFields() {
            const customFields = this.customFields ? this.customFields.map((field) => convertCustomFieldToUnlayerField(field)).filter((field) => !!field) : [];

            return customFields;
        },

        localeData() {
            return Object.keys(DEFAULT_LABELS).filter((key) => key.startsWith('unlayer.')).reduce((acc, key) => {
                acc[key.replace('unlayer.', '')] = DEFAULT_LABELS[key];

                return acc;
            }, {});
        },

        domainSettingsUrl() {
            return `https://${window.location.host}${DOMAIN_SETTINGS_URL}`;
        },

        tags() {
            return this.tagData || [];
        },

        marketingPages() {
            return this.templateGroup.templates.map((template) => {
                const pageUrl = buildMarketingPageUrl(this.currentEnv, this.tenantId, this.templateGroup.alias, template.alias, !!this.customDomain);
                const hashParams = buildHashParameters(template.tenantId, this.templateGroup.id, template.id);

                return {
                    label: template.title,
                    value: {
                        id: template.id,
                        siteId: template.templateGroupId,
                        appId: template.tenantId,
                        pageUrl: `${pageUrl}${hashParams}`,
                    },
                };
            });
        },

        isPublished() {
            if (!this.templateGroup) return false;

            return this.templateGroup.templateGroupStatus === SITE_STATUS.PUBLISHED;
        },

        canSave() {
            if (this.isSaving) return false;

            return this.hasChanges;
        },

        currentTemplate() {
            return this.templateGroup?.templates.find((template) => {
                return template.id === (this.currentPageId || this.templateGroup.templates[0].id);
            });
        },

        currentTemplateGroupContent() {
            return this.currentTemplate?.builderJson?.content;
        },

        keapLinkUrl() {
            if (!this.templateGroup) return '';

            return buildMarketingSiteUrl(this.currentEnv, this.tenantId, this.templateGroup.alias, this.customDomain);
        },

        marketingSiteEmbedCode() {
            return buildMarketingSiteEmbedCode(this.keapLinkUrl);
        },
    },

    methods: {
        async handleSaveAndExit() {
            await this.handleSave();
            window.location.href = GALLERY_ROUTE_URL;
        },
        async onCloseCustomFieldModal() {
            if (this.customFieldUnlayerResponse) {
                this.customFieldUnlayerResponse();
                this.isCreatingCustomField = false;
            }
        },

        async onCreateCustomField(newCustomField) {
            const { customFieldData } = (await createCustomField(newCustomField)).data;

            if (this.customFieldUnlayerResponse) {
                this.customFieldUnlayerResponse(convertCustomFieldToUnlayerField(customFieldData));
            }
            this.getCustomFields();
            this.isCreatingCustomField = false;
        },

        reorderTemplates(templates, mainTemplateId) {
            if (!Array.isArray(templates)) return [];

            const index = templates.findIndex((t) => t.id === mainTemplateId);

            if (index === -1) return [...templates];

            const mainTemplate = templates[index];
            const restTemplates = templates.filter((_, i) => i !== index);
            const reorderedTemp = [mainTemplate, ...restTemplates];

            return reorderedTemp;
        },

        async fetchAndInitializeTemplateGroup(siteId) {
            const { templateGroupDto } = (await getTemplateGroup(siteId)).data;

            this.templateGroup = { ...templateGroupDto };
            this.originalTemplateGroup = { ...this.templateGroup };
            this.currentPageId = this.templateGroup.templates[0].id;

            if (this.templateGroup.mainTemplateId !== this.templateGroup.templates[0].id && this.ffReorderListNoContentEnabled) {
                this.templateGroup.templates = this.reorderTemplates(this.templateGroup.templates, this.templateGroup.mainTemplateId);
            } else if (this.templateGroup?.mainTemplateId !== this.templateGroup?.templates[0].id) {
                this.sortUnlayerPages(this.templateGroup.templates.map((template) => template.id));
            }

            this.currentPageId = this.templateGroup.mainTemplateId ?? this.templateGroup.templates[0].id;
            this.isFetchingTemplateGroup = false;
        },

        async fetchCustomDomain(siteId) {
            this.loading = true;

            const data = await getCustomDomainsForTenant(siteId);

            this.loading = false;

            if (data?.setupComplete) {
                this.customDomain = {
                    dkimName: data.domainName,
                    domainName: data.domainName,
                    verified: true,
                };
            }
        },

        async getTags() {
          const { tagDTOList } = (await getContactTags()).data;

          this.tagData = tagDTOList.map((tagObj) => {
             const tagId = tagObj.id?.endsWith('L') ? Number(tagObj.id?.slice(0, -1)) : Number(tagObj.id)

            return mergeCategoryWithTag({ catName: tagObj?.category?.name, tagLabel: tagObj?.name, value: tagId})
          });
        },

        async getCustomFields() {
            const { customFieldData } = (await getCustomFields()).data;

            if (customFieldData) {
                    this.customFields = customFieldData.map((field) => ({
                        ...field,
                        options: field.options.map((option) => ({
                            ...option,
                            id: decodeHtml(option.id),
                            label: decodeHtml(option.label),
                        })),
                    }));
            }
        },

        handleCloseFeedback(feedbackSubmitted) {
            this.isFeedbackModalOpen = false;

            if (!feedbackSubmitted) {
                amplitude.infusionsoft.track(amplitude.infusionsoft.events.MARKETING_SITE_SHARE_FEEDBACK_DISMISSED,
                    AMPLITUDE_EVENT_DISMISSED_FEEDBACK);
            }
        },

        mapTemplateGroupToSite(templateGroup) {
            const pages = templateGroup?.templates.map((template) => this.mapTemplateToPage(template));

            return {
                ...templateGroup,
                name: templateGroup.title,
                slug: templateGroup.alias,
                pages,
            };
        },

        mapTemplateToPage(template) {
            return {
                ...template,
                slug: template.alias,
                name: template.title,
            };
        },

        async onUnlayerMessage({ data, postResponse }) {
            if (this.editorProvider) {
                const { keapRequestType, requestData } = data;

                switch (keapRequestType) {
                case 'getFunnelFormCount': {
                    const totalFormCount = await getTotalFormCount(this.templateGroup);

                    postResponse({ totalFormCount, prefix: 'Lead capture form' });
                    break;
                }

                case 'createFormField': {
                    this.isCreatingCustomField = true;
                    this.customFieldUnlayerResponse = postResponse;
                    break;
                }

                case 'renameSite': {
                    this.templateGroup.title = requestData.name;

                    try {
                        await this.handleSave();
                    } catch (error) {
                        postResponse({ error });
                    }
                    break;
                }

                case 'updateSiteSlug': {
                    this.templateGroup.alias = requestData.slug;

                    try {
                        await this.handleSave();
                    } catch (error) {
                        postResponse({ error });
                    }
                    break;
                }

                case 'createUnlayerPage': {
                    const page = await this.createUnlayerPage();

                    postResponse(this.mapTemplateToPage(page));
                    break;
                }

                case 'copyUnlayerPage': {
                    const copiedPage = await this.createUnlayerPage(requestData.existingPageId);

                    postResponse(this.mapTemplateToPage(copiedPage));
                    break;
                }

                case 'deleteUnlayerPage': {
                    const templates = [...this.templateGroup.templates];

                    await deleteTemplate(requestData.pageId);

                    this.templateGroup.templates = templates.filter((template) => template.id !== requestData.pageId);

                    if (this.currentPageId === requestData.pageId) {
                        this.focusPage([...this.templateGroup.templates][0].id);
                    } else this.hasChanges = true;

                    postResponse(true);
                    break;
                }

                case 'focusUnlayerPage': {
                    await this.focusPage(requestData.pageId);

                    postResponse(true);
                    break;
                }

                case 'saveUnlayerPage': {
                    await this.saveUnlayerPage(requestData, postResponse);
                    break;
                }

                case 'sortUnlayerPages': {
                    this.sortUnlayerPages(requestData.pageIds);
                    break;
                }

                case 'getMarketingSiteOptions':
                    this.marketingSitesList?.length
                        ? postResponse(
                            [
                                ...this.marketingSitesList.map((siteData) => ({
                                    label: siteData.title.replaceAll('&apos;', "'"),
                                    value: {
                                        id: siteData.id,
                                        appId: this.tenantId,
                                        siteUrl: buildMarketingSiteUrl(this.currentEnv, this.tenantId, siteData.alias, this.customDomain),
                                    },
                                })),
                            ],
                        )
                        : postResponse(
                            [
                                {
                                    label: this.$t('landingPages.noPublishedLandingPages'),
                                    value: '',
                                },
                            ],
                        );
                    break;

                case 'getMarketingPageOptions':
                    postResponse(
                        [...this.templateGroup.templates.map((page) => ({
                            label: page.title,
                            value: {
                                id: page.id,
                                siteId: page.templateGroupId,
                                appId: page.tenantId,
                                pageUrl: buildMarketingPageUrl(this.currentEnv, this.tenantId, this.templateGroup.alias, page.alias, !!this.customDomain),
                            },
                        }))],
                    );
                    break;

                case 'updateSiteSettings':
                    this.updateSiteSettings(requestData);
                    break;

                case 'uploadFavicon':
                    this.handleFaviconUpload(requestData.attachments, postResponse);
                    break;

                case 'createTagRequest': {
                    this.createTagUnlayerResponse = postResponse;
                    this.openTagModal(requestData.inputValue);
                    break;
                }

                case 'getUnlayerPages': {
                    const pages = this.marketingPages.map((page) => {
                        return {
                            id: page?.value.id,
                            name: page?.label,
                        };
                    });

                    postResponse({ pageData: pages, selectedPage: '' });
                    break;
                }

                default:
                    break;
                }
            }
        },

        async updateSiteSettings(settings) {
            this.templateGroup.settings = settings;
            this.hasChanges = true;
        },

        async saveUnlayerPage({ pageId, pageData }, postResponse) {
            const { pageName, seoDescription } = pageData;
            const pageIdx = this.templateGroup.templates.findIndex((page) => page.id === pageId);

            this.templateGroup.templates[pageIdx].title = pageName;
            this.templateGroup.templates[pageIdx].builderJson.options.seoDescription = seoDescription;

            const savedPage = await this.handleSave();

            postResponse(savedPage);
        },

        async sortUnlayerPages(pageIds) {
            const updatedTemplates = [];

            pageIds.forEach((pageId, index) => {
                const template = this.templateGroup.templates.find((_template) => _template.id === pageId);

                template.groupOrder = index;
                updatedTemplates[index] = template;
            });

            this.templateGroup.templates = updatedTemplates;
            this.templateGroup.mainTemplateId = updatedTemplates[0].id;
            this.hasChanges = true;
            this.focusPage(updatedTemplates[0].id);
        },

        async createUnlayerPage(existingPageId) {
            const pageNumber = (this.templateGroup?.templates?.length ?? 0) + 1;
            const firstPage = this.templateGroup?.templates[0];

            const copiedPage = existingPageId ? this.templateGroup.templates.find((page) => page.id === existingPageId) : null;

            const newPage = {
                templateSource: copiedPage ? copiedPage.templateSource : firstPage.templateSource,
                templateMedia: copiedPage ? copiedPage.templateMedia : firstPage.templateMedia,
                templateStatus: copiedPage ? copiedPage.templateStatus : firstPage.templateStatus,
                title: copiedPage ? `${copiedPage.title} (Copy)` : `Page ${pageNumber}`,
                alias: generatePageSlug(this.templateGroup),
                templateGroupId: this.templateGroup.id,
                groupOrder: this.templateGroup.templates.length,
                builderJson: copiedPage ? copiedPage.builderJson : {
                    content: BLANK_DESIGN,
                    options: {
                        chunks: {},
                        seoDescription: '',
                    },
                },
                formList: copiedPage ? [...(copiedPage.formList || [])] : [],
                html: copiedPage ? copiedPage.html : '<html></html>',
            };

            const { templateDto } = (await createTemplateForTemplateGroup(this.siteId, newPage)).data;

            if (copiedPage && templateDto.builderJson?.content) {
                let formCount = 0;

                const clearedPages = clearKeapFormsFromPages([{ builderJson: { content: templateDto.builderJson.content } }]);
                const designWithClearedForms = clearedPages[0].builderJson.content;

                const updatedDesign = await updateCustomFormsWithinPage({
                    page: templateDto,
                    formNamePrefix: this.templateGroup.title,
                    formNameGenerator: () => `${this.$t('marketingSites.formAutoNamePrefix')} ${++formCount}`,
                    design: convertUnlayerFormsToKeapForms(designWithClearedForms),
                });

                templateDto.builderJson.content = updatedDesign;
            }

            this.templateGroup.templates.push(templateDto);
            this.hasChanges = true;

            return this.mapTemplateToPage(templateDto);
        },

        async focusPage(pageId) {
            if (this.hasChanges) {
                await this.handleSave();
            }

            this.currentPageId = pageId;
        },


        onUpdateDesign({ data }) {
            if (data.type === 'design:migrated') { return; }
            this.currentPageData = this.editorProvider.getPageData();
            this.hasChanges = true;
        },

        goToList() {
            window.location.href = TEMPLATE_GROUP_LIST;
        },

        async handlePublish() {
            this.isPublishing = true;

            try {
                await this.handleSave();
                const { templateGroup } = await this.handleSave();

                this.isSaving = true;
                const templates = templateGroup.templates.map((template) => {
                    const { chunks } = template.builderJson.options;
                    const html = buildPageHtml(this.currentEnv, this.tenantId, templateGroup, template, chunks, template.html);

                    return { alias: template.alias, html };
                });

                const payload = {
                    id: templateGroup.id, alias: templateGroup.alias, templates, html: templateGroup.html,
                };

                await publishTemplateGroup(payload);
                await this.fetchAndInitializeTemplateGroup(this.siteId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error({ err });
                this.$error({ message: ERROR_PUBLISH });
            }

            this.isSaving = false;
            this.isPublishing = false;
        },

        handleSaveTemplate() {
            this.isSaveAsTemplateOpen = true;
        },

        openPublishTemplateGroup() {
            this.isPublishTemplateGroupOpen = true;
        },

        closePublishTemplateGroupModal() {
            this.isPublishTemplateGroupOpen = false;
        },

        closeSaveAsTemplateModal() {
            this.isSaveAsTemplateOpen = false;
            this.saveAsTemplateName = '';
        },

        async onSubmitSaveAsTemplate(templateGroup = this.templateGroup, retry = true) {
            this.isSavingTemplate = true;

            try {
                await cloneSiteFromSite(templateGroup, this.saveAsTemplateName, this.tenantId, this.currentEnv, { saveAsTemplate: true });

                this.closeSaveAsTemplateModal();
            } catch (err) {
                if (retry) {
                    // usually fails because of duplicate alias, no good way to check beforehand so add a timestamp and try one more time
                    const alias = `${this.templateGroup.alias}-${Date.now()}`;

                    await this.onSubmitSaveAsTemplate({ ...this.templateGroup, alias }, false);
                }

                this.$error({ message: ERROR_SAVE_AS_TEMPLATE });
            }

            this.isSavingTemplate = false;
        },

        handleShareFeedback() {
            this.isFeedbackModalOpen = true;
        },

        async handleSave() {
            try {
                if (this.isSaving) return false;
                this.isSaving = true;

                if (this.hasChanges) {
                    this.templateGroup.templateGroupStatus = this.templateGroup.templateGroupStatus === SITE_STATUS.PUBLISHED
                        ? SITE_STATUS.PUBLISHED_CHANGES
                        : this.templateGroup.templateGroupStatus;
                }

                const pageData = this.editorProvider?.getPageData() || { html: '<html></html>', design: BLANK_DESIGN, chunks: {} };

                const originalPage = this.templateGroup.templates.find((page) => {
                    return page.id === this.currentPageId;
                });

                const newTemplate = { ...originalPage };

                const design = this.hasChanges ? pageData.design : newTemplate.builderJson.content;

                const oldPageIdToNewPageMap = this.templateGroup.templates.reduce((map, { alias: oldTemplateAlias, id: oldTemplateId }) => {
                    map[oldTemplateId] = this.templateGroup.templates.find(({ alias }) => alias === oldTemplateAlias);

                    return map;
                }, {});

                let formCount = 0;

                let newDesign = await updateCustomFormsWithinPage({
                    page: newTemplate,
                    formNamePrefix: this.templateGroup.title,
                    formNameGenerator: () => `${this.$t('marketingSites.formAutoNamePrefix')} ${++formCount}`,
                    design: convertUnlayerFormsToKeapForms(design),
                });

                const hasCustomDomain = !!this.customDomain;
                const convertedBody = convertLinksInHtml(this.currentEnv, updateFormId(pageData.html, pageData.design), this.tenantId, this.templateGroup, oldPageIdToNewPageMap, hasCustomDomain);
                const newHtml = buildPageHtml(this.currentEnv, this.tenantId, this.templateGroup, originalPage, pageData.chunks, convertedBody);

                newDesign = convertLinksInDesign(this.currentEnv, newDesign, this.tenantId, this.templateGroup, oldPageIdToNewPageMap, hasCustomDomain);

                const updatedTemplatePayload = {
                    id: newTemplate.id,
                    builderJson: {
                        content: newDesign,
                        options: {
                            chunks: pageData.chunks,
                            seoDescription: newTemplate.builderJson.options?.seoDescription || '',
                        },
                    },
                    groupOrder: newTemplate.groupOrder,
                    title: newTemplate.title,
                    alias: newTemplate.alias,
                    templateMedia: newTemplate.templateMedia,
                    templateSource: newTemplate.templateSource,
                    templateStatus: newTemplate.templateStatus,
                    html: newHtml,
                    nonTransactionalSave: true,
                };

                const updatedTemplateGroupPayload = {
                    title: this.templateGroup.title,
                    alias: this.templateGroup.alias,
                    html: this.templateGroup.html,
                    builderJson: this.templateGroup.builderJson,
                    templateGroupStatus: this.templateGroup.templateGroupStatus,
                    templateMedia: this.templateGroup.templateMedia,
                    mainTemplateId: this.templateGroup.mainTemplateId,
                    globalTemplateId: this.templateGroup.globalTemplateId,
                    globalTemplateStatus: this.templateGroup.globalTemplateStatus,
                    settings: this.templateGroup.settings,
                };


                if (this.templateGroup.templates[0].id === this.currentPageId) {
                    updatedTemplateGroupPayload.html = updatedTemplatePayload.html;
                    updatedTemplateGroupPayload.builderJson = updatedTemplatePayload.builderJson;
                }

                const { templateDto } = (await updateTemplate(this.siteId, newTemplate.id, updatedTemplatePayload)).data;
                const { templateGroupDto } = (await updateTemplateGroup(this.siteId, updatedTemplateGroupPayload, false)).data;
                const template = { ...templateDto };
                const templateGroup = { ...templateGroupDto };

                templateGroup.templates = this.templateGroup.templates.map((_template) => {
                    if (_template.id === template.id) return template;

                    return _template;
                });

                this.templateGroup = templateGroup;

                this.isSaving = false;

                this.originalTemplateGroup = { ...updatedTemplateGroupPayload };

                this.hasChanges = false;

                return { templateGroup, template };
            } catch (err) {
                this.isSaving = false;

                return Promise.reject(err);
            }
        },

        async fetchImages({ params, done }) {
            const { FETCH_IMAGE_ERROR } = MESSAGES;

            try {
                const { data } = await axios.get('/app/fileBox/images', {
                    params: {
                        pageKey: params.page > 1 ? this.userImagesPageToken : null,
                        pageSize: IMAGES_PAGE_SIZE,
                    },
                });

                const images = data?.images.map((image) => ({
                    ...image,
                    source: 'user',
                    id: parseInt(image.id, 10),
                }));

                this.userImagesPageToken = data.nextPageKey;

                done(images, {
                    hasMore: this.userImagesPageToken != null,
                    page: params.page,
                    perPage: IMAGES_PAGE_SIZE,
                    total: data.total,
                });
            } catch (e) {
                this.displayError(FETCH_IMAGE_ERROR);
            }
        },

        async handleImageUpload({ params, done }) {
            const [file] = params.attachments;
            const formData = new FormData();
            const { UPLOAD_ERROR } = MESSAGES;

            formData.append('file', file);

            try {
                const { data } = await axios.post('/app/fileBox/images/upload', formData);

                done({ progress: 100, url: data.location });
            } catch (e) {
                this.displayError(UPLOAD_ERROR);
            }

            // TODO: Sometimes filebox will delete duplicate images, need to figure out how to best
            // present that feedback to the user
        },

        async handleImageDeleted({ params, done }) {
            const { DELETE_ERROR } = MESSAGES;

            try {
                const { data: { deleteUserUploadedImage } } = await axios.delete(
                        `/app/unlayerEmailFunnel/images/${params.id}`,
                );

                if (deleteUserUploadedImage) {
                    done();
                }
            } catch (e) {
                this.displayError(DELETE_ERROR);
            }

            // TODO: Need designs for sad path/error handling
        },

        async handleFaviconUpload(attachments, postResponse) {
            const { UPLOAD_ERROR } = MESSAGES;

            const [file] = attachments;
            const formData = new FormData();

            const blob = new Blob([file]);

            formData.append('file', blob, file.name);

            try {
                const { data } = await axios.post('/app/fileBox/images/upload', formData);

                postResponse({ faviconUrl: data.location });
            } catch (e) {
                throw new Error(UPLOAD_ERROR);
            }
        },

        registerProvider(editorProvider) {
            this.editorProvider = editorProvider;
        },

        openTagModal(tagName) {
            this.isTagModalOpen = true;
            this.tagModalTagName = tagName;
        },

        cancelCreateTag() {
            this.isTagModalOpen = false;

            if (!this.createTagUnlayerResponse) return;

            this.createTagUnlayerResponse();
        },

        handleCreateTag(tag) {
            this.isTagModalOpen = false;

            if (!this.createTagUnlayerResponse) return;

            this.createTagUnlayerResponse(mergeCategoryWithTag({ catName: tag?.category?.label, tagLabel: tag?.name, value: tag.id}));

            this.getTags();
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.marketing-site-editor-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        padding-top: 10px;
        background: #fff;
        z-index: 100;
}

.marketing-site-editor-header {
    display: flex;
    place-content: space-between;
    padding: 8px;
    width: 100%;
}

.marketing-site-editor {
    height: 100%;
    width: 100%;
}

.loading-label {
    position: fixed;
    left: 50%;
    top: 50%;
}

</style>
